import { useUploadStore } from "@components/FileUpload/uploadStore";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

const UPLOAD_STORE_KEY = "additionalDocuments";

interface UploadParams {
  files: File[];
  startIdx: number;
}

export const useUploadAdditionalDocuments = ({
  handleUploadSuccess,
  handleUploadError,
}: {
  handleUploadSuccess?: (data: { documentUrls: string[]; startIdx: number; files: File[] }) => void;
  handleUploadError?: (
    error: Error,
    meta: { startIdx: number; count: number; fileNames: string[] },
  ) => void;
}) => {
  const mutation = useMutation({
    mutationFn: ({ files, startIdx }: UploadParams) =>
      runTransaction(
        {
          module: "vessel_registration",
          screen: "AdditionalDocuments",
          action: "upload_vessel_additional_document",
          entityType: "vessel_document",
        },
        async (transactionId) => {
          const documentUrls: string[] = [];
          for (const file of files) {
            const payloadForSasToken = {
              prefix: SasTokenPrefix.VESSEL_REGISTRATION,
              contentType: file.type || null,
              privilege: SasTokenPrivilege.WRITE,
              blobUrl: null,
            };
            const { blobUrl, sasToken } = await getSasToken(
              payloadForSasToken,
              ...(transactionId ? [transactionId] : []),
            );
            const result = await uploadFileToAzureBlob({
              blobUrl,
              sasToken,
              file,
              certificateType: "ADDITIONAL_DOCUMENT",
            });
            result.forEach((r) => documentUrls.push(r.documentUrl));
          }
          return { documentUrls, startIdx, files };
        },
      ),
    onMutate: () => {
      useUploadStore.getState().beginUploadInFlight(UPLOAD_STORE_KEY);
    },
    onSuccess: (data) => handleUploadSuccess?.(data),
    onError: (error: Error, variables: UploadParams) => {
      handleUploadError?.(error, {
        startIdx: variables.startIdx,
        count: variables.files.length,
        fileNames: variables.files.map((f) => f.name),
      });
    },
    onSettled: () => {
      useUploadStore.getState().endUploadInFlight(UPLOAD_STORE_KEY);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending,
  };
};
